import type { MouseEvent, PointerEvent, SyntheticEvent, TouchEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

const LONG_PRESS_MS = 500;
const TOUCH_MOVE_CANCEL_PX = 10;

type Gesture = {
  longPressTimer: ReturnType<typeof setTimeout> | null;
  isLongPress: boolean;
  isTouchActive: boolean;
  isOpenedByTouch: boolean;
  ownsSelectionLock: boolean;
  touchOriginX: number;
  touchOriginY: number;
};

type UseLongPressTriggerArgs = {
  onShortTap: (event: MouseEvent<HTMLElement>) => void;
};

const createGesture = (): Gesture => ({
  longPressTimer: null,
  isLongPress: false,
  isTouchActive: false,
  isOpenedByTouch: false,
  ownsSelectionLock: false,
  touchOriginX: 0,
  touchOriginY: 0,
});

const clearLongPressTimer = (gesture: Gesture) => {
  if (!gesture.longPressTimer) return;
  clearTimeout(gesture.longPressTimer);
  gesture.longPressTimer = null;
};

const lockPageTextSelection = (gesture: Gesture) => {
  const root = document.documentElement;
  root.style.setProperty('user-select', 'none');
  root.style.setProperty('-webkit-user-select', 'none');
  root.style.setProperty('-webkit-touch-callout', 'none');
  gesture.ownsSelectionLock = true;
};

const releasePageTextSelection = (gesture: Gesture) => {
  if (!gesture.ownsSelectionLock) return;
  const root = document.documentElement;
  root.style.removeProperty('user-select');
  root.style.removeProperty('-webkit-user-select');
  root.style.removeProperty('-webkit-touch-callout');
  gesture.ownsSelectionLock = false;
};

const isTouchPastCancelThreshold = (gesture: Gesture, clientX: number, clientY: number) => {
  const deltaX = clientX - gesture.touchOriginX;
  const deltaY = clientY - gesture.touchOriginY;
  return deltaX * deltaX + deltaY * deltaY >= TOUCH_MOVE_CANCEL_PX * TOUCH_MOVE_CANCEL_PX;
};

export const touchSuppressionClass = 'select-none touch-manipulation';
export const touchSuppressionStyle = { WebkitTouchCallout: 'none' } as const;

export const preventNativeTouchMenu = (event: SyntheticEvent) => {
  event.preventDefault();
};

export const useLongPressTrigger = ({ onShortTap }: UseLongPressTriggerArgs) => {
  const [isOpen, setIsOpen] = useState(false);
  const gestureRef = useRef<Gesture>(createGesture());
  const gesture = gestureRef.current;

  const startLongPress = (onLongPress?: () => void) => {
    gesture.isLongPress = false;
    clearLongPressTimer(gesture);
    gesture.longPressTimer = setTimeout(() => {
      gesture.longPressTimer = null;
      gesture.isLongPress = true;
      onLongPress?.();
      setIsOpen(true);
    }, LONG_PRESS_MS);
  };

  const endTouch = () => {
    clearLongPressTimer(gesture);
    gesture.isTouchActive = false;
    if (!gesture.isOpenedByTouch) releasePageTextSelection(gesture);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) return;
    gesture.isOpenedByTouch = false;
    if (!gesture.isTouchActive) releasePageTextSelection(gesture);
  };

  useEffect(() => {
    const gestureOnUnmount = gestureRef.current;
    return () => {
      clearLongPressTimer(gestureOnUnmount);
      releasePageTextSelection(gestureOnUnmount);
    };
  }, []);

  const triggerHandlers = {
    onClick: (event: MouseEvent<HTMLElement>) => {
      if (gesture.isLongPress) {
        gesture.isLongPress = false;
        return;
      }
      onShortTap(event);
    },
    onPointerDown: (event: PointerEvent<HTMLElement>) => {
      if (event.pointerType === 'touch') return;
      startLongPress();
    },
    onPointerUp: () => clearLongPressTimer(gesture),
    onPointerLeave: () => clearLongPressTimer(gesture),
    onPointerCancel: () => clearLongPressTimer(gesture),
    onTouchStart: (event: TouchEvent<HTMLElement>) => {
      const touch = event.changedTouches[0];
      if (!touch) return;

      gesture.touchOriginX = touch.clientX;
      gesture.touchOriginY = touch.clientY;
      gesture.isTouchActive = true;
      gesture.isOpenedByTouch = false;
      lockPageTextSelection(gesture);
      startLongPress(() => {
        gesture.isOpenedByTouch = true;
        window.getSelection()?.removeAllRanges();
      });
    },
    onTouchMove: (event: TouchEvent<HTMLElement>) => {
      if (gesture.isLongPress) return;
      const touch = event.changedTouches[0];
      if (!touch || !isTouchPastCancelThreshold(gesture, touch.clientX, touch.clientY)) return;

      clearLongPressTimer(gesture);
      releasePageTextSelection(gesture);
    },
    onTouchEnd: endTouch,
    onTouchCancel: endTouch,
    onContextMenu: preventNativeTouchMenu,
  };

  return { isOpen, handleOpenChange, triggerHandlers };
};
